import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import {
	Autocomplete,
	Box,
	Chip,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { format, parse } from "date-fns";
import React from "react";

import { ExtractedMO } from "../../../../../services/ai-upload-api";
import {
	GhostButton,
	planner, // eslint-disable-line sort-imports
	staffingColor,
	staffingSoft,
	StaffingState, // eslint-disable-line sort-imports
} from "../../../planner-ui";

// Wilya status ramp instead of MUI success/warning/error (rule 7).
const statusChipSx = (state: StaffingState) => ({
	...planner.type.metaStrong,
	borderRadius: `${planner.radiusSm}px`,
	border: `1px solid ${staffingColor(state)}55`,
	bgcolor: staffingSoft(state),
	color: staffingColor(state),
});

// Tokenised form controls inside the review table.
const fieldSx = {
	"& .MuiOutlinedInput-root": {
		...planner.type.body,
		fontFamily: planner.fontMono,
		borderRadius: `${planner.radiusSm}px`,
		bgcolor: planner.surface,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.navy },
		"&.Mui-focused fieldset": { borderColor: planner.accent },
	},
	"& .MuiFormHelperText-root": { ...planner.type.meta, color: planner.inkMuted },
};

interface ReviewStepProps {
	extractedOrders: ExtractedMO[]
	products: { id: number; sku: string; name: string }[]
	workcenters: string[]
	weekStartDate: string
	onOrdersChange: (orders: ExtractedMO[]) => void
}

const ReviewStep: React.FC<ReviewStepProps> = ({
	extractedOrders,
	products,
	workcenters,
	weekStartDate,
	onOrdersChange,
}) => {
	const handleProductChange = (index: number, productId: number | null) => {
		const updated = [...extractedOrders];
		const product = products.find((p) => p.id === productId);
		updated[index] = {
			...updated[index],
			productId,
			productName: product?.name || null,
			sku: product?.sku || updated[index].rawProduct,
			isValid: validateOrder({ ...updated[index], productId }),
		};
		onOrdersChange(updated);
	};

	const handleWorkcenterChange = (index: number, workstation: string | null) => {
		const updated = [...extractedOrders];
		updated[index] = {
			...updated[index],
			workstation,
			isValid: validateOrder({ ...updated[index], workstation }),
		};
		onOrdersChange(updated);
	};

	const handleQuantityChange = (index: number, quantity: string) => {
		const updated = [...extractedOrders];
		const parsedQty = parseInt(quantity) || null;
		updated[index] = {
			...updated[index],
			quantity: parsedQty,
			isValid: validateOrder({ ...updated[index], quantity: parsedQty }),
		};
		onOrdersChange(updated);
	};

	const handleDateTimeChange = (
		index: number,
		field: "startTime" | "endTime",
		value: string
	) => {
		const updated = [...extractedOrders];
		updated[index] = {
			...updated[index],
			[field]: value,
			isValid: validateOrder({ ...updated[index], [field]: value }),
		};
		onOrdersChange(updated);
	};

	const handleRemoveOrder = (index: number) => {
		const updated = extractedOrders.filter((_, i) => i !== index);
		onOrdersChange(updated);
	};

	const handleAddOrder = () => {
		const newOrder: ExtractedMO = {
			rawProduct: "",
			rawQuantity: "",
			rawWorkstation: "",
			rawStartTime: "",
			rawEndTime: "",
			productId: null,
			productName: null,
			sku: "",
			quantity: null,
			workstation: null,
			startTime: null,
			endTime: null,
			confidence: 0,
			isValid: false,
			validationErrors: ["Please fill in all required fields"],
		};
		onOrdersChange([...extractedOrders, newOrder]);
	};

	const validateOrder = (order: Partial<ExtractedMO>): boolean => {
		return !!(
			order.productId &&
			order.quantity &&
			order.quantity > 0 &&
			order.workstation &&
			order.startTime
		);
	};

	// Confidence rides the staffing ramp so it matches the rest of the planner.
	const getConfidenceState = (confidence: number): StaffingState => {
		if (confidence >= 90) return "staffed";
		if (confidence >= 70) return "partial";
		return "unstaffed";
	};

	const formatDateTimeForInput = (isoString: string | null): string => {
		if (!isoString) return "";
		try {
			const date = new Date(isoString);
			return format(date, "yyyy-MM-dd'T'HH:mm");
		} catch {
			return "";
		}
	};

	const validCount = extractedOrders.filter((o) => o.isValid).length;
	const invalidCount = extractedOrders.length - validCount;

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Box display="flex" gap={1}>
					<Chip
						label={`${extractedOrders.length} orders found`}
						variant="outlined"
						sx={{
							...planner.type.metaStrong,
							borderRadius: `${planner.radiusSm}px`,
							borderColor: planner.hairlineStrong,
							bgcolor: planner.surfaceSubtle,
							color: planner.inkSecondary,
						}}
					/>
					{validCount > 0 && (
						<Chip label={`${validCount} valid`} size="small" sx={statusChipSx("staffed")} />
					)}
					{invalidCount > 0 && (
						<Chip
							label={`${invalidCount} need attention`}
							size="small"
							sx={statusChipSx("partial")}
						/>
					)}
				</Box>
				<GhostButton
					startIcon={<AddIcon sx={{ fontSize: 16 }} />}
					onClick={handleAddOrder}
					size="small"
				>
					Add Order
				</GhostButton>
			</Box>

			<TableContainer
				component={Paper}
				elevation={0}
				sx={{
					maxHeight: 400,
					border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
					borderRadius: `${planner.radius}px`,
				}}
			>
				<Table stickyHeader size="small">
					<TableHead>
						<TableRow
							sx={{
								"& .MuiTableCell-root": {
									...planner.labelCaps,
									bgcolor: planner.surfaceSubtle,
									color: planner.inkSecondary,
									borderBottomColor: planner.hairline,
								},
							}}
						>
							<TableCell width={40}></TableCell>
							<TableCell>Product</TableCell>
							<TableCell width={100}>Quantity</TableCell>
							<TableCell>Workcenter</TableCell>
							<TableCell>Start Time</TableCell>
							<TableCell>End Time</TableCell>
							<TableCell width={80}>Confidence</TableCell>
							<TableCell width={50}></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{extractedOrders.map((order, index) => (
							<TableRow
								key={index}
								sx={{
									bgcolor: order.isValid ? "inherit" : planner.partialSoft,
									"& .MuiTableCell-root": { borderBottomColor: planner.hairline },
								}}
							>
								<TableCell>
									{!order.isValid ? (
										<Tooltip title={order.validationErrors?.join(", ") || "Invalid"}>
											<WarningIcon sx={{ color: planner.partial }} fontSize="small" />
										</Tooltip>
									) : (
										<Typography sx={{ color: planner.staffed }}>✓</Typography>
									)}
								</TableCell>

								<TableCell>
									<Autocomplete
										size="small"
										options={products}
										value={products.find((p) => p.id === order.productId) || null}
										onChange={(_, newValue) =>
											handleProductChange(index, newValue?.id || null)
										}
										getOptionLabel={(option) =>
											`${option.sku} - ${option.name}`
										}
										renderInput={(params) => (
											<TextField
												{...params}
												placeholder="Select product"
												variant="outlined"
												size="small"
												helperText={order.rawProduct ? `In the file: "${order.rawProduct}"` : undefined}
												FormHelperTextProps={{ sx: { fontSize: "10px", m: 0 } }}
											/>
										)}
										sx={{ minWidth: 200, ...fieldSx }}
									/>
								</TableCell>

								<TableCell>
									<TextField
										size="small"
										type="number"
										value={order.quantity || ""}
										onChange={(e) => handleQuantityChange(index, e.target.value)}
										placeholder={order.rawQuantity || "Qty"}
										inputProps={{ min: 1 }}
										sx={{ width: 80, ...fieldSx }}
									/>
								</TableCell>

								<TableCell>
									<Autocomplete
										size="small"
										options={workcenters}
										value={order.workstation || null}
										onChange={(_, newValue) =>
											handleWorkcenterChange(index, newValue)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												placeholder="Select"
												variant="outlined"
												size="small"
											/>
										)}
										sx={{ minWidth: 120, ...fieldSx }}
									/>
								</TableCell>

								<TableCell>
									<TextField
										size="small"
										type="datetime-local"
										value={formatDateTimeForInput(order.startTime)}
										onChange={(e) =>
											handleDateTimeChange(index, "startTime", e.target.value)
										}
										InputLabelProps={{ shrink: true }}
										sx={{ width: 180, ...fieldSx }}
									/>
								</TableCell>

								<TableCell>
									<TextField
										size="small"
										type="datetime-local"
										value={formatDateTimeForInput(order.endTime)}
										onChange={(e) =>
											handleDateTimeChange(index, "endTime", e.target.value)
										}
										InputLabelProps={{ shrink: true }}
										sx={{ width: 180, ...fieldSx }}
									/>
								</TableCell>

								<TableCell>
									{order.confidence > 0 && (
										<Chip
											label={`${order.confidence}%`}
											size="small"
											variant="outlined"
											sx={{
												...statusChipSx(getConfidenceState(order.confidence)),
												fontFamily: planner.fontMono,
											}}
										/>
									)}
								</TableCell>

								<TableCell>
									<IconButton
										size="small"
										onClick={() => handleRemoveOrder(index)}
										sx={{ color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						))}

						{extractedOrders.length === 0 && (
							<TableRow>
								<TableCell colSpan={8} align="center" sx={{ py: 4 }}>
									<Typography sx={{ ...planner.type.body, color: planner.inkSecondary }}>
										No orders extracted. Click &quot;Add Order&quot; to manually add one.
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{extractedOrders.length > 0 && invalidCount > 0 && (
				<Box mt={2}>
					<Typography sx={{ ...planner.type.body, color: planner.partial }}>
						<WarningIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
						{invalidCount} order{invalidCount !== 1 ? "s" : ""} need attention.
						Please select a product, workstation, and start time for each order.
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default ReviewStep;


